import type { Peer, PeerConnection } from "@/utils/peer"
import { Sentry } from "./sentry"

const sentryContext = new Map<string, Record<string, unknown>>()
const peerRegistry = new Set<PeerConnection<Peer>>()
const sentryContextTargetRegistry = new Set<SentryContextTarget>()

export interface SentryContext {
	type: string
	data: Record<string, unknown>
}

export interface SentryContextTarget {
	setSentryContext(context: SentryContext): void
	disableSentry?(): void
}

export function registerPeer(peer: PeerConnection<Peer>) {
	peerRegistry.add(peer)
}

export function unregisterPeer(peer: PeerConnection<Peer>) {
	peerRegistry.delete(peer)
}

/**
 * Initialize Peers with any previously set Sentry context
 */
export function initializePeerSentryContext(peer: PeerConnection<Peer>) {
	Array.from(sentryContext.entries()).map(([type, data]) => peer.setSentryContext(type, data))
}

export function registerSentryContextTarget(target: SentryContextTarget) {
	sentryContextTargetRegistry.add(target)
	initializeSentryContextTarget(target)
}

export function unregisterSentryContextTarget(target: SentryContextTarget) {
	sentryContextTargetRegistry.delete(target)
}

export function initializeSentryContextTarget(target: SentryContextTarget) {
	Array.from(sentryContext.entries()).map(([type, data]) =>
		target.setSentryContext({ type, data }),
	)
}

export function setLocalSentryContext(context: SentryContext) {
	sentryContext.set(context.type, context.data)
	context.type === "user"
		? Sentry.setUser(context.data)
		: Sentry.setContext(context.type, context.data)
	Array.from(sentryContextTargetRegistry).map((target) => target.setSentryContext(context))
}

/*
 * Update Sentry context both in main thread and across all workers
 */
export function setGlobalSentryContext(type: string, data: Record<string, unknown>) {
	try {
		setLocalSentryContext({ type, data })
		Array.from(peerRegistry).map((peer) => peer.setSentryContext(type, data))
	} catch (e) {
		Sentry.captureException(new Error("Failed to update global Sentry context", { cause: e }))
	}
}

export function disableSentryLocally() {
	void Sentry.getCurrentScope().getClient()?.close()
	Array.from(sentryContextTargetRegistry).map((target) => target.disableSentry?.())
}

/**
 * Drop Sentry exceptions from the main thread and all workers
 */
export function disableSentryGlobally() {
	disableSentryLocally()
	Array.from(peerRegistry).map((peer) => peer.disableSentry())
}
